import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { AppStoreState, FilterTag } from 'types';
import { clearResourceFilters, setResourceFilters, toggleTag } from 'state/modules/app';
import CharacterGrid from 'components/Resources/CharacterGrid/CharacterGrid';
import CheckBox from 'components/@common/CheckBox/CheckBox';
import FilterDropDown from 'components/@common/Dropdowns/FilterDropDown';
import { fetchFilterTags } from 'state/modules/entity/custom/resource-favorites-mutations/thunks/resource-thunks';
import { Button, InputAdornment, TextField } from '@mui/material';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { translate } from 'translations/translator';
import { FONT_GSS_FUTURA } from 'themes/default';
import { updateEntity } from 'state/modules/entity';
const BUTTON = 'button';
const t = key => translate('modals', key);

const Grid = ({ filterTags, selectedTags, handleFilterToggle }: any): any => (
  <CharacterGrid
    items={filterTags}
    selectedItems={selectedTags}
    onSelectItem={handleFilterToggle}
  />
);

const DynamicFilters = ({ filterTags, selectedTags, handleFilterToggle }: any): any =>
  filterTags[0].componentType === BUTTON ? (
    <Grid
      filterTags={filterTags}
      handleFilterToggle={handleFilterToggle}
      selectedTags={selectedTags}
    />
  ) : (
    filterTags.map(
      (filterTag: FilterTag, index: number): React.ReactElement => (
        <CheckBox
          key={index}
          label={filterTag.value}
          checked={selectedTags.findIndex(tag => tag.id === filterTag.id) !== -1 || false}
          onChange={() => handleFilterToggle(filterTag)}
        />
      )
    )
  );

const DynamicFilterGroups = ({
  filterGroups,
  setOpenFilter,
  openFilter,
  selectedTags,
  handleFilterToggle
}): any => {
  const classes = useStyles();

  return filterGroups.map(
    (filterGroup: any, index: number): React.ReactElement => (
      <FilterDropDown
        key={index}
        label={filterGroup.filter}
        setOpenFilter={setOpenFilter}
        openFilter={openFilter}
        selectedTags={selectedTags}
        filterGroupValues={filterGroup.values}
        contentClasses={classes.filterDropDown}
        applySectionClass={classes.sectionClass}
        openByDefault={
          filterGroup.values.find(filter => selectedTags.indexOf(filter.id) >= 0) !== undefined
        }
      >
        <DynamicFilters
          selectedTags={selectedTags}
          filterTags={filterGroup.values}
          handleFilterToggle={handleFilterToggle}
        />
      </FilterDropDown>
    )
  );
};

export const AddActivitiesFilter = () => {
  const classes = useStyles();
  const dispatch = useDispatch() as any;
  const { selectedTags } = useSelector(({ app }: AppStoreState) => app.resource.selectedFilters);
  const [openFilter, setOpenFilter] = useState('');
  const { filterGroups } = useSelector(({ entity }: AppStoreState) => entity);
  const [displayedSearchText, setDisplayedSearchText] = useState('');
  const translateResources = key => translate('resources', key);

  const resetFilters = useCallback(() => {
    dispatch(clearResourceFilters());
    dispatch(setResourceFilters('', []));
    setDisplayedSearchText('');
  }, [dispatch, setDisplayedSearchText]);

  React.useEffect(() => {
    if (!filterGroups.length) dispatch(fetchFilterTags());

    return () => {
      resetFilters();
    };
  }, [dispatch, resetFilters, filterGroups.length]);

  // reset on unmount
  React.useEffect(() => {
    return () => {
      dispatch(updateEntity({ filterGroups: [] }));
    }; // eslint-disable-next-line
  }, []);

  const handleFilterToggle = (filter: any) => {
    dispatch(toggleTag(filter));
  };

  const setSearchText = (newSearchText: string) => {
    setDisplayedSearchText(newSearchText);
    if (newSearchText.length > 2 || displayedSearchText !== '') {
      dispatch(setResourceFilters(newSearchText, []));
    }
  };

  return (
    <>
      <div className={classes.filterGroups}>
        <DynamicFilterGroups
          handleFilterToggle={handleFilterToggle}
          filterGroups={filterGroups}
          setOpenFilter={setOpenFilter}
          openFilter={openFilter}
          selectedTags={selectedTags}
        />

        <div className={classes.searchBar}>
          <TextField
            onChange={event => setSearchText(event.target.value)}
            value={displayedSearchText}
            placeholder={translateResources('search')}
            fullWidth={true}
            variant="outlined"
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px'
            }}
            InputProps={{
              disableUnderline: true,
              style: {
                width: '250px',
                height: '43px',
                fontWeight: '400 !important',
                border: '2px solid',
                borderRadius: '8px'
              },
              endAdornment: (
                <InputAdornment position="end">
                  <FontAwesomeIcon icon={faSearch} />
                </InputAdornment>
              )
            }}
          />
        </div>
        <div className={classes.clearFilters}>
          <Button
            startIcon={<FontAwesomeIcon icon={faTimes} size="lg" />}
            style={{ width: '140px', margin: '10px', verticalAlign: 'center' }}
            onClick={resetFilters}
          >
            <span style={{ marginTop: 5 }}>{t('clearFilters')}</span>
          </Button>
        </div>
      </div>
    </>
  );
};

const useStyles = makeStyles(() => ({
  filterGroups: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-evenly'
  },
  filterDropDown: {
    width: '130px',
    border: '2px solid #000000',
    borderRadius: '5px'
  },
  sectionClass: {
    padding: '0 !important',
    margin: '1px 10px -3px 10px',
    width: '110px !important',
    fontFamily: 'unset !important',
    fontWeight: 300,
    color: '#202020 !important',
    opacity: 0.5
  },
  clearText: {
    color: '#2878C1',
    textDecoration: 'underline'
  },
  btnClass: {
    border: '2px solid #FFFFFF',
    borderRadius: '3px',
    backgroundColor: '#FFFFFF',
    height: '37px',
    width: '37px',
    marginTop: '10px',
    marginRight: '15px',
    userSelect: 'none',
    alignSelf: 'center',
    '&:focus': {
      outline: 'none',
      border: '2px solid #FFFFFF'
    },
    '&:active': {
      outline: 'none',
      border: '3px solid #FFFFFF'
    }
  },
  searchBar: {
    position: 'absolute',
    right: 60,
    top: 7,
    width: '250px'
  },
  clearFilters: {
    paddingTop: '15px',
    fontSize: '12px',
    lineHeight: '18px',
    fontFamily: FONT_GSS_FUTURA
  }
}));
